import axios from 'axios';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

function authHeaders(token: string) {
  return { headers: { Authorization: `Bearer ${token}` } };
}

// ─── Sprint Report Types ─────────────────────────────────────────

export interface SprintReportItem {
  id: string;
  title: string;
  story_type: string | null;
  story_points: number | null;
  priority: string;
  status: string;
  completed_at: string | null;
  board_column_id: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  column_name: string | null;
  column_type: string | null;
}

export interface SprintReportSummary {
  total_items: number;
  completed_count: number;
  carried_over_count: number;
  velocity: number;
  planned_points: number;
  completion_rate: number;
}

export interface BurndownPoint {
  date: string;
  remaining: number | null;
  ideal: number;
}

export interface SprintReportData {
  sprint: {
    id: string;
    name: string;
    goal: string | null;
    status: string;
    sprint_number: number;
    start_date: string;
    end_date: string;
    started_at: string | null;
    completed_at: string | null;
    project_name: string | null;
    project_code: string | null;
    team_name: string | null;
  };
  summary: SprintReportSummary;
  previous_summary: SprintReportSummary | null;
  burndown: BurndownPoint[];
  burndown_points: BurndownPoint[];
  burndown_items: BurndownPoint[];
  burndown_unit: 'points' | 'items';
  completed_items: SprintReportItem[];
  carried_over_items: SprintReportItem[];
}

// ─── Leadership Report Types ─────────────────────────────────────

export interface ProjectApprovalBreakdown {
  approved: number;
  pending: number;
  rejected: number;
}

export interface ProjectBoardBreakdown {
  todo: number;
  in_progress: number;
  blocked: number;
  done: number;
  done_not_worked: number;
}

export interface LeadershipProject {
  id: string;
  name: string;
  project_code: string;
  project_status: string;
  start_date: string | null;
  end_date: string | null;
  owner_id: string | null;
  owner_name: string | null;
  owner_email: string | null;
  total_requirements: number;
  approval_breakdown: ProjectApprovalBreakdown;
  board_breakdown: ProjectBoardBreakdown;
  progress_percent: number;
}

export interface LeadershipTotals {
  total_projects: number;
  total_requirements: number;
  approved: number;
  pending: number;
  rejected: number;
  board_todo: number;
  board_in_progress: number;
  board_blocked: number;
  board_done: number;
  board_done_not_worked: number;
}

export interface LeadershipReportData {
  totals: LeadershipTotals;
  projects: LeadershipProject[];
}

export interface ProjectRequirement {
  id: string;
  title: string;
  approval_status: string;
  priority: string;
  story_type: string | null;
  story_points: number | null;
  completed_at: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  board_column_id: string | null;
  workflow_column_name: string | null;
  workflow_column_type: string | null;
}

export interface SprintOption {
  id: string;
  name: string;
  status: string;
  sprint_number: number;
  project_name: string;
  project_code: string;
  project_id: string;
  start_date: string;
  end_date: string;
  started_at: string | null;
  completed_at: string | null;
  team_id: string | null;
  team_name: string | null;
}

// ─── Requirement Aging Report Types ──────────────────────────────

export interface AgingRequirement {
  id: string;
  title: string;
  priority: string;
  status: string;
  story_type: string | null;
  story_points: number | null;
  created_at: string;
  approved_at: string | null;
  actual_start_date: string | null;
  days_since_created: number;
  days_since_approved: number | null;
  days_since_in_progress: number | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  project_name: string;
}

export interface AgingReportData {
  requirements: AgingRequirement[];
  users: { id: string; name: string }[];
}

// ─── Velocity Report Types ───────────────────────────────────────

export interface VelocitySprintData {
  sprint_id: string;
  sprint_name: string;
  sprint_number: number;
  status: string;
  start_date: string;
  end_date: string;
  started_at: string | null;
  completed_at: string | null;
  total_items: number;
  completed_items: number;
  completed_points: number;
  planned_points: number;
}

export interface VelocityReportData {
  sprints: VelocitySprintData[];
  average_velocity: number;
}

// ─── Time Tracking Report Types ─────────────────────────────────

export interface TimeTrackingEntry {
  id: string;
  hours: number;
  description: string | null;
  logged_date: string;
  requirement_id: string;
  requirement_title: string;
  requirement_code: string | null;
  project_id: string;
  project_name: string;
  project_code: string;
  user_name: string;
}

export interface TimeTrackingSprintOption {
  id: string;
  name: string;
  status: string;
  start_date: string;
  end_date: string;
}

export interface TimeTrackingReportData {
  total_hours: number;
  entries: TimeTrackingEntry[];
  sprints: TimeTrackingSprintOption[];
  users: { id: string; name: string }[];
}

// ─── API Functions ───────────────────────────────────────────────

export async function getSprintReport(sprintId: string, token: string): Promise<SprintReportData> {
  const res = await axios.get(`${API_BASE}/reports/sprint/${sprintId}`, authHeaders(token));
  return res.data.data;
}

export async function getLeadershipReport(token: string, options?: { includeSubtasks?: boolean }): Promise<LeadershipReportData> {
  const params = options?.includeSubtasks ? '?includeSubtasks=true' : '';
  const res = await axios.get(`${API_BASE}/reports/leadership${params}`, authHeaders(token));
  return res.data.data;
}

export interface BlockedRequirement {
  id: string;
  title: string;
  requirement_code: string;
  project_name: string;
  project_code: string;
  owner_name: string | null;
}

export async function getBlockedRequirements(token: string): Promise<BlockedRequirement[]> {
  const res = await axios.get(`${API_BASE}/reports/leadership/blocked`, authHeaders(token));
  return res.data.data;
}

export async function getProjectRequirements(projectId: string, token: string): Promise<ProjectRequirement[]> {
  const res = await axios.get(`${API_BASE}/reports/leadership/projects/${projectId}/requirements`, authHeaders(token));
  return res.data.data;
}

export async function getRequirementAgingReport(token: string, assignedTo?: string): Promise<AgingReportData> {
  let url = `${API_BASE}/reports/requirement-aging`;
  if (assignedTo) {
    url += `?assigned_to=${assignedTo}`;
  }
  const res = await axios.get(url, authHeaders(token));
  return res.data.data;
}

export async function getAllSprints(token: string, projectId?: string): Promise<SprintOption[]> {
  let url = `${API_BASE}/reports/sprints`;
  if (projectId) {
    url += `?project_id=${projectId}`;
  }
  const res = await axios.get(url, authHeaders(token));
  return res.data.data;
}

export async function getVelocityReport(token: string): Promise<VelocityReportData> {
  const res = await axios.get(`${API_BASE}/reports/velocity`, authHeaders(token));
  return res.data.data;
}

export async function getTimeTrackingReport(
  token: string,
  sprintId?: string,
  startDate?: string,
  endDate?: string,
  userId?: string
): Promise<TimeTrackingReportData> {
  const params = new URLSearchParams();
  if (sprintId) params.set('sprint_id', sprintId);
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  if (userId) params.set('user_id', userId);
  const qs = params.toString();
  const url = `${API_BASE}/reports/time-tracking${qs ? `?${qs}` : ''}`;
  const res = await axios.get(url, authHeaders(token));
  return res.data.data;
}

export async function getProjectsCreatedTrend(token: string): Promise<RequirementsTrendPoint[]> {
  const res = await axios.get(`${API_BASE}/reports/projects-trend`, authHeaders(token));
  return res.data.data;
}

export interface RequirementsTrendPoint {
  date: string;
  count: number;
}

export async function getRequirementsCreatedTrend(days: number, token: string, startDate?: string): Promise<RequirementsTrendPoint[]> {
  const params = new URLSearchParams();
  if (startDate) {
    params.set('start_date', startDate);
  } else {
    params.set('days', String(days));
  }
  const res = await axios.get(`${API_BASE}/reports/requirements-trend?${params.toString()}`, authHeaders(token));
  return res.data.data;
}

export async function getProjectRequirementTrends(token: string): Promise<Record<string, RequirementsTrendPoint[]>> {
  const res = await axios.get(`${API_BASE}/reports/project-requirement-trends`, authHeaders(token));
  return res.data.data;
}

export async function getMyTeamIds(token: string): Promise<string[]> {
  const res = await axios.get(`${API_BASE}/reports/my-teams`, authHeaders(token));
  return res.data.data;
}
